import { useEffect, useState } from 'react'
import { Button, Card, Container, Modal, Row, Table } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import KLayerNavigation from '../components/KLayerNavigation'
import { apiRequest, isUserLoggedIn } from '../helpers'
import { t } from '../locale'

type IdentityDoc = {
  _id: string
  createdAt: string
  identityCompanyId: string
  identityRequestId: string
  hasPdf: boolean
  hasVideo: boolean
}

export default function IdentityDocuments() {
  const { clientId, subjectId } = useParams()
  const [docs, setDocs] = useState<IdentityDoc[]>([])
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!clientId || !subjectId) return

    apiRequest(
      `${process.env.REACT_APP_API_URL}/identity/documents?clientId=${clientId}&subjectId=${subjectId}`,
      'GET'
    )
      .then((res: any) => {
        if (res?.ok && Array.isArray(res.docs)) {
          setDocs(res.docs)
        }
      })
      .catch((err: any) => {
        console.error('Failed to load identity docs', err)
      })
  }, [clientId, subjectId])

  const openPdf = (doc: IdentityDoc) => {
    const subdomain = window.subdomain ? `&subdomain=${encodeURIComponent(window.subdomain)}` : ''
    const url = `${process.env.REACT_APP_API_URL}/identity/ds-identity/pdf?companyId=${encodeURIComponent(doc.identityCompanyId)}&requestId=${encodeURIComponent(doc.identityRequestId)}${subdomain}`
    setPreviewUrl(url)
  }

  const openVideo = (doc: IdentityDoc) => {
    const subdomain = window.subdomain ? `&subdomain=${encodeURIComponent(window.subdomain)}` : ''
    const url = `${process.env.REACT_APP_API_URL}/identity/ds-identity/video?companyId=${encodeURIComponent(doc.identityCompanyId)}&requestId=${encodeURIComponent(doc.identityRequestId)}${subdomain}`
    setPreviewUrl(url)
  }

  return (
    <Container>
      <Row>
        <div className='sidebar col-md-3 col-lg-2 p-0'>
          <KLayerNavigation logged={isUserLoggedIn()} />
        </div>
        <main className='col-md-9 col-lg-10 ps-2 ps-md-3 px-md-4'>
          <Card className='border-0 mb-4'>
            <Card.Body>
              <h4 className='fw-bold mb-3'>
                {t('DS | Identity')} - {t('documents')}
              </h4>
              <hr />

              {docs.length === 0 ? (
                <p className='text-muted'>{t('No additional schemas') || 'No documents found.'}</p>
              ) : (
                <Table striped hover responsive>
                  <thead>
                    <tr>
                      <th>{t('datumHochladen')}</th>
                      <th>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docs.map(doc => (
                      <tr key={doc._id}>
                        <td>{new Date(doc.createdAt).toLocaleString()}</td>
                        <td>
                          {doc.hasPdf && (
                            <Button
                              variant='outline-secondary'
                              size='sm'
                              className='me-2'
                              onClick={() => openPdf(doc)}
                            >
                              PDF
                            </Button>
                          )}
                          {doc.hasVideo && (
                            <Button
                              variant='outline-primary'
                              size='sm'
                              onClick={() => openVideo(doc)}
                            >
                              Video
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </main>
      </Row>

      <Modal show={!!previewUrl} onHide={() => setPreviewUrl(null)} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('preview')}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: '80vh' }}>
          {previewUrl && (
            <iframe
              src={previewUrl}
              title='Identity Preview'
              width='100%'
              height='100%'
              style={{ border: 'none' }}
            />
          )}
        </Modal.Body>
      </Modal>
    </Container>
  )
}
